import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize, Subject, takeUntil } from 'rxjs';
import { Dayjs } from 'dayjs';
import { CoachCalendarService } from '../../../core/services/coach-calendar.service';
import { CoachStudentService } from '../../../core/services/coach-student.service';
import { IWeeklyMeetingStatus } from '../../../core/models/appointment.model';

interface IStudentOption {
  id: number;
  name: string;
}

@Component({
  selector: 'coach-appointment-form-modal',
  templateUrl: './appointment-form-modal.component.html',
  styleUrl: './appointment-form-modal.component.scss',
})
export class AppointmentFormModalComponent implements OnInit, OnDestroy {
  @Input() startsAt!: Dayjs;
  /** Filled in when planning starts from a student's header instead of an empty cell. */
  @Input() studentId?: number;

  fg: FormGroup = this.formBuilder.group({
    studentId: [null, [Validators.required]],
    title: [null, [Validators.required, Validators.maxLength(255)]],
    notes: [null, [Validators.maxLength(1000)]],
  });

  students: IStudentOption[] = [];
  weeklyStatus?: IWeeklyMeetingStatus;
  errKey?: string;

  isLoading = false;
  isSubmitted = false;

  destroyed$ = new Subject();

  constructor(
    public modal: NgbActiveModal,
    private formBuilder: FormBuilder,
    private calendarService: CoachCalendarService,
    private studentService: CoachStudentService,
  ) {
  }

  ngOnInit(): void {
    this.getStudents();
  }

  getStudents() {
    this.studentService.getStudents('?page=1&size=100')
      .pipe(takeUntil(this.destroyed$))
      .subscribe(r => {
        const assignments = (r.data?.result ?? r.data) as any[];
        // The roster comes back as assignments, each carrying the student it points at.
        this.students = assignments
          .map(item => item.student ?? item)
          .filter(student => !!student)
          .map(student => ({id: student.id, name: student.name ?? student.username}));

        if (this.studentId && this.students.some(student => student.id === this.studentId)) {
          this.fg.patchValue({studentId: this.studentId});
          this.onStudentChanged(this.studentId);
        }
      });
  }

  onStudentChanged(studentId?: number) {
    this.weeklyStatus = undefined;
    if (!studentId) {
      return;
    }

    this.calendarService.getWeeklyStatus(studentId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(r => {
        this.weeklyStatus = r.data as IWeeklyMeetingStatus;
      });
  }

  onSubmit() {
    this.isSubmitted = true;
    this.errKey = undefined;

    if (this.fg.invalid) {
      return;
    }

    this.isLoading = true;
    const formVal = this.fg.value;

    this.calendarService.createAppointment({
      studentId: formVal.studentId,
      title: formVal.title,
      startsAt: this.startsAt.toISOString(),
      notes: formVal.notes ?? undefined,
    })
      .pipe(takeUntil(this.destroyed$), finalize(() => this.isLoading = false))
      .subscribe(() => {
        this.modal.close(true);
      }, error => {
        // The API rejects a slot outside availability (400) or already taken (409); both need a
        // different sentence than a generic failure.
        this.errKey = (error?.code === 409) ? 'agenda.errors.slot_taken' : 'agenda.errors.create_failed';
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
